import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { AccountsType } from "constants/accountsType";

interface DisableAccountsReceivableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isUpdating: boolean;
  type?: AccountsType;
}

const CONTENT_MAP = {
  receivable: {
    title: "Disable Auto-Manage Accounts Receivable?",
    description:
      "This will disable automatic tracking of Accounts Receivable for new invoices. Any existing receivables will still be cleared when corresponding transactions are linked.",
    bannerDescription: (
      <span>
        <b>Auto-Defer & Recognize Revenue</b> is bundled with this setting.
        Turning it off will also stop generating monthly revenue schedules from
        invoices.
      </span>
    ),
  },
  payable: {
    title: "Disable Auto-Manage Accounts Payable?",
    description:
      "This will disable automatic tracking of Accounts Payable for new bills. Any existing payables will still be cleared when corresponding transactions are linked.",
    bannerDescription: (
      <span>
        <b>Auto-Defer & Recognize Expense</b> is bundled with this setting.
        Turning it off will also stop spreading bill costs across their service
        periods.
      </span>
    ),
  },
} as const;

export const DisableAccountsReceivableModal = ({
  isOpen,
  onClose,
  onConfirm,
  isUpdating,
  type = "receivable",
}: DisableAccountsReceivableModalProps) => {
  const content = CONTENT_MAP[type];

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>{content.title}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <div className="t-text-body-sm t-text-text-60 t-mb-0 t-flex t-flex-col t-gap-4">
            <span>{content.description}</span>
            <Banner type="warning" description={content.bannerDescription} />
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            customType="danger"
            onClick={onConfirm}
            isLoading={isUpdating}
            disabled={isUpdating}
          >
            Disable
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
